<template>
  <div class="fixed inset-0 flex flex-col h-[100svh] bg-white font-sans overflow-hidden pb-[64px]">

    <header class="flex-none h-16 bg-white border-b border-blue-50 shadow-sm z-50 flex items-center gap-3 px-4">
      <button @click="$router.push('/')" class="text-gray-400 hover:text-blue-500 transition">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <div class="relative cursor-pointer flex-none" @click="showProfile = true">
        <img :src="img" class="w-10 h-10 rounded-full object-cover border border-blue-100" @contextmenu.prevent @click.stop/>
        <div
          class="absolute bottom-0 right-0 w-3.5 h-3.5 border-2 border-white rounded-full transition-all duration-500"
          :class="loadingIA ? 'bg-blue-400 animate-pulse' : (isOnline ? 'bg-green-500' : 'bg-gray-400')"
        ></div>
      </div>

      <div class="flex-1 min-w-0" @click="showProfile = true">
        <h3 class="font-bold text-gray-800 text-sm truncate">
          {{ name }}<span v-if="age" class="font-normal text-gray-400">, {{ age }} años</span>
        </h3>
        <p class="text-[10px] font-bold transition-all duration-300 uppercase tracking-wider"
           :class="loadingIA ? 'text-blue-500 translate-x-1' : (isOnline ? 'text-green-500' : 'text-gray-400')">
          {{ loadingIA ? 'Escribiendo...' : (isOnline ? 'En línea ahora' : 'Vuelve pronto') }}
        </p>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-pink-50/30 to-white flex flex-col items-start min-h-0 w-full">
        <div class="space-y-4 pb-10 w-full flex flex-col items-start">
            <div
            v-for="(msg, i) in mensajes"
            :key="i"
            :class="[
                'max-w-[85%] rounded-2xl text-sm mb-1 shadow-sm transition-all duration-300 inline-block',
                // Estilos específicos según el rol
                msg.role === 'user'
                ? 'bg-main text-white self-end rounded-tr-none p-3.5'
                : msg.role === 'system_alert' // Cambié is_alert por system_alert para coincidir con tu PHP
                ? 'bg-red-600 text-white self-center text-center font-black border-2 border-yellow-400 my-4 py-2 w-full animate-pulse'
                : msg.role === 'assistant_photo'
                ? 'bg-white p-1 self-start rounded-tl-none border border-pink-100' // Padding menor para que la foto luzca más
                : 'bg-white text-gray-700 self-start rounded-tl-none border border-pink-100 p-3.5'
            ]"
            >
            <template v-if="msg.role === 'assistant_photo'">
                <div class="relative group overflow-hidden rounded-xl cursor-zoom-in" @click="openPhoto(msg.content)">
                    <img
                    :src="msg.content"
                    class="max-w-full h-auto min-w-[200px] max-h-[350px] object-cover block transition-transform duration-300 group-hover:scale-105 select-none"
                    @load="scrollToBottom"
                    loading="lazy"
                    @contextmenu.prevent
                    @dragstart.prevent
                    @click.stop
                    />
                    <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span class="text-white text-2xl">🔍</span>
                    </div>
                </div>
            </template>

            <template v-else>
                {{ msg.content }}
            </template>
            </div>

            <div v-if="loadingIA" class="self-start bg-white p-3 px-4 rounded-2xl rounded-tl-none border border-blue-50 shadow-sm inline-block">
            <div class="flex gap-1.5">
                <div class="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></div>
                <div class="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                <div class="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
            </div>
            </div>
        </div>
        <div ref="scrollAnchor" class="h-2 flex-none w-full"></div>
    </main>

    <footer class="flex-none bg-white border-t border-pink-50 z-50">
      <div class="p-3">
        <div class="flex items-center gap-2 bg-gray-50 p-2 rounded-2xl border border-pink-50 transition-all"
             :class="{'opacity-50 pointer-events-none': !isOnline || cooldownActive > 0}">
          <input
            v-model="nuevoMensaje"
            @keyup.enter="enviar"
            @focus="handleFocus"
            type="text"
            :disabled="loadingIA || !isOnline || cooldownActive > 0"
            placeholder="Escribe un mensaje..."
            class="flex-1 bg-transparent border-none focus:ring-0 text-sm px-2 outline-none"
          />
          <button
            @click="enviar"
            :disabled="loadingIA || !nuevoMensaje.trim() || !isOnline || cooldownActive > 0"
            class="bg-main text-white p-2.5 rounded-xl transition-all shadow-sm"
          >
             <span v-if="cooldownActive > 0" class="text-xs font-bold">{{ cooldownActive }}s</span>
             <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
             </svg>
          </button>
        </div>
      </div>
    </footer>

    <transition name="fade">
    <div v-if="showProfile"
         class="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-xl bg-black/40"
         @click.self="showProfile = false">

      <div class="bg-gray-900 w-full max-w-sm sm:rounded-[3rem] h-[90vh] sm:h-auto overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.3)] relative animate-slide-up border-t sm:border border-white/10">

        <div class="absolute inset-0 flex overflow-x-auto snap-x snap-mandatory no-scrollbar bg-black"
             @scroll="onScrollGallery">

          <div class="flex-shrink-0 w-full h-full snap-start relative">
            <img :src="img" class="w-full h-full object-cover object-top opacity-90" @contextmenu.prevent @click.stop/>
          </div>

          <div v-for="(photo, index) in (isPremium ? allPics : allPics.slice(0, 2))"
               :key="index"
               class="flex-shrink-0 w-full h-full snap-start relative">

            <img :src="photo" class="w-full h-full object-cover object-top opacity-90" @contextmenu.prevent @click.stop/>

            <div v-if="!isPremium && index === 1 && allPics.length > 1"
                 class="absolute inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center p-10 text-center pointer-events-none">
              <span class="text-4xl mb-3">🔒</span>
              <p class="text-white font-black uppercase text-xs tracking-[0.2em]">Contenido Exclusivo</p>
              <p class="text-white text-[10px] mt-2 font-medium leading-tight">Hazte Premium para ver el álbum completo de {{ name }}.</p>
            </div>
          </div>
        </div>

        <div class="absolute top-6 left-0 right-0 px-10 flex gap-1.5 z-40 pointer-events-none">
          <div v-for="n in (isPremium ? allPics.length + 1 : Math.min(allPics.length + 1, 3))"
               :key="n"
               class="h-1 flex-1 rounded-full overflow-hidden transition-colors duration-300"
               :class="(activePhotoIndex === n - 1) ? 'bg-white' : 'bg-white/20'">
          </div>
        </div>

        <div class="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent pointer-events-none"></div>

        <button @click="showProfile = false" class="absolute top-6 right-6 z-50 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white p-3 rounded-full border border-white/20 transition-all">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div class="absolute bottom-0 left-0 right-0 p-8 pt-20 bg-gradient-to-t from-black via-black/90 to-transparent pointer-events-none transition-all duration-500">
          <div class="pointer-events-auto">

            <div class="mb-4">
              <div v-if="isOnline" class="inline-flex items-center gap-2 bg-green-500/20 backdrop-blur-xl px-4 py-1.5 rounded-full border border-green-500/30">
                <span class="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-[0_0_10px_#4ade80]"></span>
                <span class="text-[11px] text-white font-black uppercase tracking-[0.1em]">En línea ahora</span>
              </div>
            </div>

            <h3 class="font-black text-white tracking-tighter transition-all duration-500"
                :class="activePhotoIndex === 0 ? 'text-5xl mb-2' : 'text-3xl mb-0'">
              {{ name }}<span class="font-thin text-white/60" :class="activePhotoIndex === 0 ? 'ml-3' : 'ml-2 text-xl'">{{ age }} años</span>
            </h3>

            <div v-if="activePhotoIndex === 0" class="relative mb-6 animate-fade-in">
              <p class="text-white/90 text-lg leading-snug font-medium-small pr-4 italic">
                "{{ bio || '¡Hola! Escribime para que nos conozcamos un poquito más... 😉' }}"
              </p>
            </div>

            <div v-else class="h-4"></div>

            <button v-if="activePhotoIndex === 0"  @click="showProfile = false"
                    class="group relative w-full bg-white text-black py-6 rounded-[2rem] transition-all duration-300 active:scale-95 flex items-center justify-center gap-4 overflow-hidden shadow-[0_25px_50px_rgba(0,0,0,0.4)]">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <span class="relative z-10 text-xl font-extrabold tracking-tight">Seguir chateando</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 relative z-10 text-blue-500 transition-transform group-hover:translate-x-1" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd" />
              </svg>
            </button>

            <div class="h-[80px] sm:h-0"></div>
          </div>
        </div>
      </div>
    </div>
  </transition>
  <transition name="fade">
    <div v-if="photoZoom" class="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" @click="closePhoto">
        <div class="relative max-w-full max-h-full flex items-center justify-center">
            <img :src="photoZoom" class="max-w-full max-h-full rounded-lg" @click.stop />

            <div class="absolute inset-0 z-10 w-full h-full cursor-default" @contextmenu.prevent @click.stop></div>
        </div>

        <button class="absolute top-6 right-6 text-white z-20" @click="closePhoto">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
    </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/api'
import Swal from 'sweetalert2'
const route = useRoute()

// Estado
const allPics = ref([])
const characterId = ref(route.params.id)
const name = ref('Cargando...')
const img = ref('')
const bio = ref('')
const age = ref(null)
const isOnline = ref(true)
const showProfile = ref(false)
const nuevoMensaje = ref('')
const scrollAnchor = ref(null)
const loadingIA = ref(false)
const mensajes = ref([])
const cooldownActive = ref(0)
let cooldownTimer = null
const isPremium = ref(false)
const photoZoom = ref(null) // Guardará la URL de la foto abierta
const openPhoto = (url) => {
  photoZoom.value = url
}
const closePhoto = () => {
  photoZoom.value = null
}
const startCooldown = (seconds) => {
  cooldownActive.value = seconds
  clearInterval(cooldownTimer)
  cooldownTimer = setInterval(() => {
    if (cooldownActive.value > 0) {
      cooldownActive.value--
    } else {
      clearInterval(cooldownTimer)
    }
  }, 1000)
}

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const scrollToBottom = () => {
  nextTick(() => {
    scrollAnchor.value?.scrollIntoView({ behavior: 'smooth' })
  })
}

// Manejo de foco para móviles
const handleFocus = () => {
  setTimeout(() => {
    scrollToBottom()
  }, 300)
}

const activePhotoIndex = ref(0)

const onScrollGallery = (e) => {
  const scrollLeft = e.target.scrollLeft
  const width = e.target.offsetWidth
  // Calculamos el índice basado en el scroll
  activePhotoIndex.value = Math.round(scrollLeft / width)
}

const inicializarChat = async (id) => {
  try {
    const storageUser = localStorage.getItem('user')
    if (storageUser) {
      const userObj = JSON.parse(storageUser)
      isPremium.value = userObj.is_premium == 1
    }
    const res = await api.get(`/ai_characters/${id}`)
    const data = res.data
    let fotosArray = []
    if (data.gallery && typeof data.gallery === 'string') {
      fotosArray = data.gallery.split(',').map(url => url.replace(/\\\//g, '/'))
      allPics.value = [data.photo_url.replace(/\\\//g, '/'), ...fotosArray]
    } else {
      allPics.value = []
    }
    name.value = data.name
    bio.value = data.bio
    age.value = data.age
    img.value = data.photo_url
    isOnline.value = data.is_online == 1
    const resChat = await api.get(`/chat?character_id=${id}&limit=30`)
    if (resChat.data) {
      mensajes.value = resChat.data.map(m => ({
        role: m.role,
        content: m.text || m.content,
      }))
    }
    scrollToBottom()
  } catch (error) { console.error(error) }
}

const enviar = async () => {
  if (!nuevoMensaje.value.trim() || loadingIA.value || !isOnline.value || cooldownActive.value > 0) return

  const userText = nuevoMensaje.value
  const userData = JSON.parse(localStorage.getItem('user'))

  mensajes.value.push({ role: 'user', content: userText })
  nuevoMensaje.value = ''
  scrollToBottom()

  loadingIA.value = false

  try {
    const response = await api.post('/chat', {
      user_id: userData.id,
      character_id: characterId.value,
      message: userText
    })

    if(!isPremium){
        startCooldown(response.data?.wait_time || 10)
    }
    let aiResponse = response.data?.ai_response || ""
    let photoUrl = response.data?.photo_url || null
    // --- LÓGICA DE ESCRITURA CON PAUSAS (1 a 5 pausas) ---

    // 1. Pausa inicial de lectura (Siempre ocurre)
    await wait(1000 + Math.random() * 2000)

    // 2. Decidir cuántas pausas tendrá (entre 1 y 5)
    // Si el mensaje es muy cortito (menos de 15 letras), forzamos a 1 pausa para no exagerar
    const numPausas = aiResponse.length < 20 ? 1 : Math.floor(Math.random() * 5) + 1

    // Dividimos el tiempo total de escritura entre el número de pausas
    const tiempoTotalEscritura = Math.min(aiResponse.length * 100, 10000)
    const tiempoPorTramo = tiempoTotalEscritura / numPausas

    for (let i = 0; i < numPausas; i++) {
      // Aparece "Escribiendo..."
      loadingIA.value = true
      scrollToBottom()

      // Escribe por un momento
      const variacionTramo = (Math.random() * 0.5 + 0.75); // Variación entre 75% y 125%
      await wait(tiempoPorTramo * variacionTramo)
      //await wait(tiempoPorTramo)

      // Se detiene (Desaparece el loadingIA)
      loadingIA.value = false

      // Pausa de "pensar" o "dudar" (entre 0.5s y 1.5s)
      // No pausamos en el último tramo para que el mensaje salga fluido al final
      if (i < numPausas - 1) {
        await wait(1500 + Math.random() * 2500)
      }
    }
    // --- FIN DE SIMULACIÓN ---

    // Validaciones finales
    if (response.data?.is_online === false) {
      isOnline.value = false
      mensajes.value.push({ role: 'assistant', content: aiResponse || "Vuelvo en un rato..." })
      return
    }

    const frasesBloqueo = ["Lo siento, pero no puedo", "I cannot fulfill", "No puedo continuar", "políticas de seguridad"]
    if (aiResponse && frasesBloqueo.some(f => aiResponse.includes(f))) {
      mensajes.value.push({ role: 'is_alert', content: "⚠️ MODERE SU VOCABULARIO O SERA BANEADO" })
      return
    }

    if (!aiResponse || aiResponse === "") {
      Swal.fire({
        icon: 'error',
        title: 'Error de enlace',
        toast: true,
        html: '<span style="font-size: 12px;">Se perdió la conexión. Intenta de nuevo.</span>',
        position: 'top-end',
        showConfirmButton: false,
        customClass: {
            popup: 'border-l-4 border-red-600 shadow-lg'
        },
        timer: 5000,
        timerProgressBar: true,
        background: '#1a1a1a',
        color: '#ffffff'
      });
    }else {
        // Si todo está bien, mensaje normal de la asistente
        mensajes.value.push({
            role: 'assistant',
            content: aiResponse
        });
    }

    if (photoUrl) {
      // Opcional: Podrías volver a poner loadingIA = true un segundo
      // para simular que está "adjuntando" el archivo
      await wait(2000)

      mensajes.value.push({
        role: 'assistant_photo',
        content: photoUrl
      })

      // El scroll se ejecuta cuando la imagen carga (por el @load en el HTML)
      setTimeout(() => scrollToBottom(), 100);
    }
  } catch (error) {
    if (error.response?.status === 429) {
      startCooldown(error.response.data.wait_time || 10)
    }
  } finally {
    loadingIA.value = false
    scrollToBottom()
  }
}

onUnmounted(() => clearInterval(cooldownTimer))
onMounted(() => { inicializarChat(characterId.value) })
watch(() => route.params.id, (newId) => { if (newId) { characterId.value = newId; inicializarChat(newId); } })
</script>

<style scoped>
@keyframes bounceCustom {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
.animate-bounce { animation: bounceCustom 0.8s infinite; }

/* Evitar zoom automático en iOS */
@media screen and (max-width: 768px) {
  input { font-size: 16px !important; }
}
html, body {
  overflow: hidden;
  height: 100%;
}
/* ELEVACIÓN PARA MÓVIL: Sube el input sobre el navbar */
.safe-area-bottom {
  /* Sumamos espacio extra para que no quede pegado a la barra de navegación del sistema */
  padding-bottom: calc(env(safe-area-inset-bottom) + 1rem);
}

.animate-slide-up {
  animation: slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slide-up {
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.animate-zoom-in {
  animation: zoomIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes zoomIn {
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.cursor-zoom-in {
  cursor: zoom-in;
}
</style>